import { debounce, isEmpty, set } from 'lodash';
import { Container } from '@material-ui/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useHistory,
  useParams,
} from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  isCreateMode,
  isConsultMode,
  isUpdateMode,
  isNoneMode,
  isBackgroundCreateMode,
} from '../../../../utils/store/store-utils';
import {
  selectMode,
  selectAereo,
  setModel,
  resetModel,
  addToList,
  selectBackgroundMode,
  selectHouseType,
  success,
  failure,
  setError,
  loading,
  setResponse,
  changeToCreateMode,
  changeToUpdateMode,
  changeToConsultMode,
  fetchByIdAsync,
  selectOperationHouse,
  setOperationImportacao,
  setOperationExportacao,
  setHouseHawbType,
  setHouseHblType,
} from '../../../../features/house/HouseSlice';
import { register } from '../../../../features/house/houseAPI';
import QCXMainLayout from '../../../../components/main-layout/QCXMainLayout';
import QCXHouseWizardFinalForm from '../../../../components/house/QCXHouseWizardFinalForm';
import { getBreadcrumbsOfHouse, getTitleOfHouse } from '../../../../features/house/houseUtils';
import { normalizeNumeral } from '../../../../utils/general/general-utils';



function createHouseRegistrationConfig({
  operationValue, 
  typeValue, 
  basePath, 
  breadcrumbModulo,
}) {
  return {
    operationValue,
    typeValue,
    basePath,
    breadcrumbModulo,
  };
}

const labelModulo = (t, operacao, modal, tipo) => {
  const opLabel = operacao === 'EXPORTACAO'
    ? t('com.muralis.qcx.exportacao.label')
    : t('com.muralis.qcx.importacao.label');

  const modalLabel = modal === 'MARITIMO' ? 'Marítimo' : 'Aéreo';
  const tipoLabel = tipo === 'HBL' ? 'HBL' : 'HAWB';

  return `${opLabel} ${modalLabel} ${tipoLabel}`;
};

function HouseRegistrationPageBase({ authInfo = {}, config }) {
  const history = useHistory();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { id } = useParams();

  const mode = useSelector(selectMode);

  const aereo = useSelector(selectAereo);

  const type = useSelector(selectHouseType);
  const operation = useSelector(selectOperationHouse);

  const backgroundMode = useSelector(selectBackgroundMode);

  const [tipoEmbarque, setTipoEmbarque] = useState(null);

  const [cargaPerigosa, setCargaPerigosa] = useState(null);

  const fetchById = useCallback((houseId) => (
    dispatch(fetchByIdAsync(houseId))
  ), [dispatch]);


  useEffect(() => {
    if (config.operationValue === 'EXPORTACAO') {
      dispatch(setOperationExportacao());
    } else {
      dispatch(setOperationImportacao());
    }

    if (config.typeValue === 'HBL') {
      dispatch(setHouseHblType());
    } else if (config.typeValue === 'HAWB') {
      dispatch(setHouseHawbType());
    }
  }, [dispatch, config.operationValue, config.typeValue]);

  const isNone = useMemo(() => (
    isNoneMode(mode)
  ), [mode]);

  const isCreate = useMemo(() => (
    isCreateMode(mode)
  ), [mode]);

  const isConsult = useMemo(() => (
    isConsultMode(mode)
  ), [mode]);

  const isUpdate = useMemo(() => (
    isUpdateMode(mode)
  ), [mode]);

  const actionName = useMemo(() => {
    if (isCreate || isNone) {
      return t('com.muralis.qcx.acoes.nova');
    }
    if (isConsult) {
      return t('com.muralis.qcx.acoes.visualizar');
    }
    return t('com.muralis.qcx.acoes.alterar');
  }, [isNone, isCreate, isConsult, t]);

  const pageTitle = useMemo(() =>
  (
    isNone || isCreate
      ? getTitleOfHouse(type)
      : t('com.muralis.qcx.despesa.grupoDespesaReceitaExistente', { ID: aereo?.id })
  )
    , [
      isNone,
      isCreate,
      aereo,
      type,
      t,
    ]);

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
  };

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  const handleCancelUpdate = () => {
    if (id) {
      fetchById(id);
    }
    handleChangeToConsult();
  };


  const breadcrumbs = useMemo(() => {
    const nomeModulo = typeof config.breadcrumbModulo?.name === 'function'
      ? config.breadcrumbModulo.name(t)
      : config.breadcrumbModulo?.name;

    return ([
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        link: {
          to: config.breadcrumbModulo?.to,
          name: nomeModulo,
        },
      },
      {
        text: {
          name: getBreadcrumbsOfHouse(type),
        },
      },
      {
        text: {
          name: actionName,
        },
      },
    ]);
  }, [t, config.breadcrumbModulo, type, actionName]);


  useEffect(() => {
    if (!isEmpty(id)) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id, dispatch]);

  const handleAlternativeSave = async (event, step, next) => {
    const normalizedData = normalize(aereo);
  };

  const handleChangeToBackgroundCreate = () => { };

  const handleResetBackgroundMode = () => { };

  const create = async (data) => {
    const executeDebounced = debounce(async () => {
      try {
        const response = await register(data);

        if (response?.status === 201) {
          dispatch(resetModel());

          const created = response?.data;

          handleDispatchSetModel(created);

          dispatch(addToList({ data: created }));

          const handleResultWithDebounce = debounce(() => {
            history.push(config.basePath);

            dispatch(success());

            dispatch(setResponse({
              status: response.status,
              data: created,
              message: t('com.muralis.qcx.mensagem.masterCadastrada'),
            }));
          }, 500);

          handleResultWithDebounce();
        }
      } catch (error) {
        const defaultErrorMessage = t('com.muralis.qcx.erro.erroCadastrarMaster', { descricao: error?.response?.data?.message });

        const errorMessage = error?.response?.data?.message
          ? `${defaultErrorMessage}: ${error?.response?.data?.message}.`
          : `${defaultErrorMessage}.`;

        dispatch(failure());

        dispatch(setError({
          message: errorMessage,
        }));
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const unnormalize = useCallback((data) => {
    const unnormalizedData = {
      ...data,
    };

    setTipoEmbarque(data?.tipoEmbarque);
    setCargaPerigosa(isSim(data?.cargaPerigosa));

    return unnormalizedData;
  }, []);

  const isTrue = (value) => value === 'SIM';

  const isSim = (value) => (value ? 'SIM' : 'NAO');

  const normalize = useCallback((data) => {
    const volumes = data?.volumes?.map((obj) => ({
      id: 0,
      quantidadeVolumes: normalizeNumeral(obj?.quantidadeVolumes),
      pesoBruto: normalizeNumeral(obj?.pesoBruto),
      comprimento: normalizeNumeral(obj?.comprimento),
      altura: normalizeNumeral(obj?.altura),
      largura: normalizeNumeral(obj?.largura),
      chargeableWeight: normalizeNumeral(obj?.chargeableWeight),
      descricaoMercadoria: obj?.descricaoMercadoria,
      numeroContainer: '',
      unidadeDeMedida: null,
      numeroLacre: '',
    }));

    const custos = data?.custos?.map((obj) => ({
      ...obj,
      id: 0,
      despesaReceita: {
        id: obj?.despesaReceita?.id,
      },
      moeda: {
        id: obj?.moeda?.id,
      },
      tipoPagamento: obj?.tipoPagamento,
      valorCompraMoeda: normalizeNumeral(obj?.valorCompraMoeda ?? 0),
      valorCompraReais: normalizeNumeral(obj?.valorCompraReais ?? 0),
      valorCompraDolar: normalizeNumeral(obj?.valorCompraDolar ?? 0),
      valorVendaMoeda: normalizeNumeral(obj?.valorVendaMoeda ?? 0),
      valorVendaReais: normalizeNumeral(obj?.valorVendaReais ?? 0),
      valorVendaDolar: normalizeNumeral(obj?.valorVendaDolar ?? 0),
      declarado: isTrue(obj?.declarado ?? 'NAO'),
      profit: isTrue(obj?.profit ?? 'NAO'),
      percentualOrigem: normalizeNumeral(obj?.percentualOrigem ?? 0),
      percentualDestino: normalizeNumeral(obj?.percentualDestino ?? 0),
      valorProfitOrigemMoeda: normalizeNumeral(obj?.valorProfitOrigemMoeda ?? 0),
      valorProfitOrigemReais: normalizeNumeral(obj?.valorProfitOrigemReais ?? 0),
      valorProfitOrigemDolar: normalizeNumeral(obj?.valorProfitOrigemDolar ?? 0),
      valorProfitDestinoMoeda: normalizeNumeral(obj?.valorProfitDestinoMoeda ?? 0),
      valorProfitDestinoReais: normalizeNumeral(obj?.valorProfitDestinoReais ?? 0),
      valorProfitDestinoDolar: normalizeNumeral(obj?.valorProfitDestinoDolar ?? 0),
    }));

    const normalizeData = {
      numeroProcesso: data?.numeroProcesso,
      cliente: {
        id: data?.cliente?.id,
      },
      servico: {
        id: data?.servico?.id,
      },
      type,
      consignatario: {
        id: data?.consignatario?.id,
      },
      exportador: { id: data?.exportador?.id ?? null },
      tipoNotify: data?.tipoNotify,
      notifyId: data?.notifyId,
      incotermVenda: {
        id: data?.incotermVenda?.id,
      },
      agenteCarga: {
        id: data?.agenteCarga?.id,
      },
      tarifa: data?.tarifa,
      tabelaFrete: {
        id: data?.tabelaFrete?.id,
      },
      origem: {
        id: data?.origem?.id,
      },
      destino: {
        id: data?.destino?.id,
      },
      tipoEmbarque,
      cargaPerigosa: isTrue(cargaPerigosa),
      aeroportoDesembarque: {
        id: data?.aeroportoDesembarque?.id,
      },
      numeroVooViagem: data?.numeroVooViagem,
      aeroportoEmbarque: {
        id: data?.aeroportoEmbarque?.id,
      },
      transportadorInternacional: {
        id: data?.transportadorInternacional?.id,
      },
      volumes,
      custos,
      ncms: null,
      embarcacao: null,
      operacao: operation,
    };

    return normalizeData;
  }, [type, tipoEmbarque, cargaPerigosa, operation]);

  const handleSubmit = async (data, step, next) => {
    const isLastStep = step === 2;

    if (isLastStep) {
      const payload = normalize(data);
      await create(payload);
    } else {
      next();
    }
  };

  const model = useMemo(() => unnormalize(aereo), [
    isCreate,
    aereo,
    unnormalize,
  ]);

  const isBackgroundCreate = useMemo(() => isBackgroundCreateMode(backgroundMode), [backgroundMode]);

  const handleDispatchSetModel = useCallback(
    (data) => {
      dispatch(setModel(unnormalize(data)));
    },
    [dispatch, unnormalize]
  );

  return (
    <QCXMainLayout pageTitle={pageTitle} breadcrumbs={breadcrumbs} authInfo={authInfo}>
      <Container style={{ display: 'flex', padding: '0 1rem 3rem 1rem' }}>
        <QCXHouseWizardFinalForm
          isCreate={isCreate}
          isConsult={isConsult}
          isUpdate={isUpdate}
          isBackgroundCreate={isBackgroundCreate}
          model={model}
          handleChangeModel={handleDispatchSetModel}
          handleSubmit={handleSubmit}
          handleChangeToCreate={handleChangeToCreate}
          handleChangeToConsult={handleChangeToConsult}
          handleAlternativeSave={handleAlternativeSave}
          handleChangeToUpdate={handleChangeToUpdate}
          handleCancelUpdate={handleCancelUpdate}
          handleChangeToBackgroundCreate={handleChangeToBackgroundCreate}
          handleResetBackgroundMode={handleResetBackgroundMode}
          authInfo={authInfo}
          requiredRoles={['cliente']}
          tipoEmbarque={tipoEmbarque}
          setTipoEmbarque={setTipoEmbarque}
          cargaPerigosa={cargaPerigosa}
          setCargaPerigosa={setCargaPerigosa}
          type={type}
        />
      </Container>
    </QCXMainLayout>
  );
}


// IMPORTAÇÃO AÉREO HAWB
export function ImportacaoAereoHouseRegistrationPage({ authInfo = {} }) {
  const basePath = '/importacao/agenciamento/aereo/hawb';

  const config = createHouseRegistrationConfig({
    operationValue: 'IMPORTACAO',
    typeValue: 'HAWB',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'IMPORTACAO', 'AEREO', 'HAWB'),
    },
  });

  return <HouseRegistrationPageBase authInfo={authInfo} config={config} />;
}

// IMPORTAÇÃO MARÍTIMO HBL
export function ImportacaoMaritimoHouseRegistrationPage({ authInfo = {} }) {
  const basePath = '/importacao/agenciamento/maritimo/hbl';

  const config = createHouseRegistrationConfig({
    operationValue: 'IMPORTACAO',
    typeValue: 'HBL',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'IMPORTACAO', 'MARITIMO', 'HBL'),
    },
  });

  return <HouseRegistrationPageBase authInfo={authInfo} config={config} />;
}

// EXPORTAÇÃO AÉREO HAWB
export function ExportacaoAereoHouseRegistrationPage({ authInfo = {} }) {
  const basePath = '/exportacao/agenciamento/aereo/hawb';

  const config = createHouseRegistrationConfig({
    operationValue: 'EXPORTACAO',
    typeValue: 'HAWB',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'EXPORTACAO', 'AEREO', 'HAWB'),
    },
  });

  return <HouseRegistrationPageBase authInfo={authInfo} config={config} />;
}

// EXPORTAÇÃO MARÍTIMO HBL
export function ExportacaoMaritimoHouseRegistrationPage({ authInfo = {} }) {
  const basePath = '/exportacao/agenciamento/maritimo/hbl';

  const config = createHouseRegistrationConfig({
    operationValue: 'EXPORTACAO',
    typeValue: 'HBL',
    basePath,
    breadcrumbModulo: {
      to: basePath,
      name: (t) => labelModulo(t, 'EXPORTACAO', 'MARITIMO', 'HBL'),
    },
  });

  return <HouseRegistrationPageBase authInfo={authInfo} config={config} />;
}


export default ImportacaoAereoHouseRegistrationPage;
